import { useNavigate } from '@tanstack/react-router'
import { Button } from '@workspace/ui/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog'
import { Clock, Filter, Link2, Sparkles } from 'lucide-react'
import { useSetOnboardingRoleMutation } from '../../../hooks/api'

export interface InvestorCtaModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function InvestorCtaModal({ open, onOpenChange }: InvestorCtaModalProps) {
  const navigate = useNavigate()
  const setRoleMutation = useSetOnboardingRoleMutation({
    onSuccess: () => {
      onOpenChange(false)
      navigate({ to: '/join' })
    },
  })
  const steps = [
    { icon: Filter, label: 'Add your investment thesis (sector, stage, geography)' },
    { icon: Link2, label: 'Embed link in X, LinkedIn bio, fund website or email signature' },
    { icon: Sparkles, label: 'Founders submit → auto-screened → ready diligence on best matches' },
  ]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md gap-0 p-0">
        <DialogHeader className="px-4 sm:px-6 pt-6 pb-4">
          <DialogTitle className="text-base sm:text-lg">
            Want to try SeedForge while you evaluate the investment?
          </DialogTitle>
          <DialogDescription className="sr-only">
            Set up your custom investor link to receive filtered dealflow
          </DialogDescription>
        </DialogHeader>

        <div className="px-4 sm:px-6 pb-6 space-y-5 overflow-hidden">
          {/* Custom Link Preview */}
          <div className="flex items-center gap-3 rounded-xl border border-border bg-input/50 px-3 sm:px-4 py-3">
            <div className="flex items-center justify-center size-8 sm:size-9 rounded-lg bg-[#f97316]/10 shrink-0">
              <Link2 className="size-4 text-[#f97316]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground">Your custom link</p>
              <p className="font-mono text-xs sm:text-sm text-white truncate">
                pitch.seedforge.com/[your-fund-name]
              </p>
            </div>
          </div>

          {/* Setup Steps */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Clock className="size-3.5 text-muted-foreground shrink-0" />
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Setup (30 seconds)
              </p>
            </div>
            <div className="space-y-2">
              {steps.map((step, index) => (
                <div
                  key={step.label}
                  className="flex items-start gap-2.5 sm:gap-3 rounded-xl border border-border bg-input/50 px-3 sm:px-3.5 py-2.5 sm:py-3"
                >
                  <div className="flex items-center justify-center size-5 sm:size-6 rounded-md bg-elevated text-xs font-medium text-muted-foreground shrink-0 mt-0.5">
                    {index + 1}
                  </div>
                  <p className="text-xs sm:text-sm text-white/90 leading-relaxed min-w-0">
                    {step.label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Benefit */}
          <p className="text-xs sm:text-sm text-center text-muted-foreground">
            Start seeing <span className="text-white font-medium">filtered dealflow</span> with
            ready diligence within <span className="text-white font-medium">48 hours</span>.
          </p>

          {/* CTA */}
          <Button
            className="w-full"
            size="lg"
            disabled={setRoleMutation.isPending}
            onClick={() => setRoleMutation.mutate({ body: { role: 'investor' } })}
          >
            {setRoleMutation.isPending ? 'Setting up...' : 'Setup My Link'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
